// src/Staff/InviteDealer.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE;

export default function InviteDealer() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("staffToken") || "", []);

  // 🔒 Enforce staff login
  useEffect(() => {
    if (!token) navigate("/staff/login", { replace: true });
  }, [token, navigate]);

  // Applied filters (used for querying)
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [invitedFilter, setInvitedFilter] = useState("all"); // all | invited | not
  const [q, setQ] = useState("");

  // Draft filters (UI only until Apply)
  const [draftState, setDraftState] = useState("");
  const [draftCity, setDraftCity] = useState("");
  const [draftInvited, setDraftInvited] = useState("all");
  const [draftQ, setDraftQ] = useState("");

  // Paging
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);

  // Data/UI
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  // Inline email edits
  const [emailEdits, setEmailEdits] = useState({});
  const [savingEmailId, setSavingEmailId] = useState(null);
  const [invitingId, setInvitingId] = useState(null);

  // Helper to read PascalCase or camelCase
  const g = (obj, Key) => obj?.[Key] ?? obj?.[Key[0].toLowerCase() + Key.slice(1)];

  // Only load on page/pageSize/token changes (not while typing)
  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, token]);

  const authHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  const applyFilters = async () => {
    setStateFilter(draftState.trim());
    setCityFilter(draftCity.trim());
    setInvitedFilter(draftInvited);
    setQ(draftQ.trim());
    setPage(1);
    await load({
      override: {
        state: draftState.trim(),
        city: draftCity.trim(),
        invited: draftInvited,
        q: draftQ.trim(),
        page: 1,
      },
    });
  };

  const load = async (opts = {}) => {
    if (!token) return; // hard stop if not authenticated
    setLoading(true);
    setErr("");
    try {
      if (!API) throw new Error("REACT_APP_API_BASE is not set.");

      const state = opts.override?.state ?? stateFilter;
      const city = opts.override?.city ?? cityFilter;
      const invited = opts.override?.invited ?? invitedFilter;
      const query = opts.override?.q ?? q;
      const pageNum = opts.override?.page ?? page;

      const qs = new URLSearchParams({
        page: String(pageNum),
        pageSize: String(pageSize),
        invited: invited || "all",
      });
      if (state) qs.set("state", state);
      if (city) qs.set("city", city);
      if (query) qs.set("q", query);

      const res = await fetch(`${API}/staff/dealers?${qs.toString()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${text}`);
      }

      const data = await res.json();
      const items = (data.items || []).map((r) => ({ ...r, inviteStatus: deriveInviteStatus(r) }));
      setRows(items);
      setTotal(data.total || 0);

      const initial = {};
      for (const r of items) {
        const id = g(r, "Id");
        initial[id] = g(r, "Email") || g(r, "LatestInviteEmail") || "";
      }
      setEmailEdits(initial);
    } catch (e) {
      setErr("Could not load dealers. " + (e?.message || ""));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const deriveInviteStatus = (r) => {
    if (!g(r, "LatestInviteId")) return "Not invited";
    if (g(r, "LatestInviteUsedAt")) return "Accepted";
    if (g(r, "LatestInviteExpiresAt") && new Date(g(r, "LatestInviteExpiresAt")) < new Date())
      return "Expired";
    return "Invited";
  };

  const updateEmail = async (dealerId) => {
    const email = (emailEdits[dealerId] || "").trim();
    if (!email) return setErr("Email cannot be empty.");
    setSavingEmailId(dealerId);
    setErr(""); setNote("");
    try {
      const res = await fetch(`${API}/staff/dealers/${dealerId}/email`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNote("Email updated.");
      await load();
    } catch (e) {
      setErr("Could not update email. " + (e?.message || ""));
    } finally {
      setSavingEmailId(null);
    }
  };

  const sendInvite = async (dealerId) => {
    const email = (emailEdits[dealerId] || "").trim();
    setInvitingId(dealerId);
    setErr(""); setNote("");
    try {
      const body = email ? { Email: email } : {};
      const res = await fetch(`${API}/staff/dealers/${dealerId}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${text}`);
      }
      const data = await res.json();
      const invitedTo = data.invite?.Email || data.invite?.email || email || "dealer";
      setNote(`Invite sent to ${invitedTo}.`);
      await load();
    } catch (e) {
      setErr("Could not send invite. " + (e?.message || ""));
    } finally {
      setInvitingId(null);
    }
  };

  const totalPages = Math.min(9999, Math.max(1, Math.ceil(total / pageSize)));

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 className="text-2xl font-semibold">Invite Dealers (Staff)</h1>
        <div className="flex gap-2">
            <button
              onClick={() => navigate("/staff")}
              className="h-10 px-4 text-sm rounded-lg border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100"
            >
              Staff Home
            </button>
          <button
            onClick={() => load()}
            className="h-10 px-4 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Alerts */}
      {err && (
        <div className="mb-3 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-800">
          {err}
        </div>
      )}
      {note && (
        <div className="mb-3 rounded-lg border border-blue-300 bg-blue-100 px-3 py-2 text-sm text-blue-800">
          {note}
        </div>
      )}

      {/* Filters (Apply triggers fetch) */}
      <div className="bg-white border rounded-xl p-4 mb-3 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">State</label>
          <input
            value={draftState}
            onChange={(e) => setDraftState(e.target.value)}
            placeholder="e.g., FL"
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs text-gray-600 mb-1">City</label>
          <input
            value={draftCity}
            onChange={(e) => setDraftCity(e.target.value)}
            placeholder="e.g., Miami"
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs text-gray-600 mb-1">Invited Status</label>
          <select
            value={draftInvited}
            onChange={(e) => setDraftInvited(e.target.value)}
            className="h-10 w-full border border-gray-300 rounded-lg px-3 text-sm"
          >
            <option value="all">All</option>
            <option value="invited">Invited</option>
            <option value="not">Not invited</option>
          </select>
        </div>
        <div className="md:col-span-4">
          <label className="block text-xs text-gray-600 mb-1">Search</label>
          <div className="flex gap-2">
            <input
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="Name, email, city, state"
              className="h-10 flex-1 border border-gray-300 rounded-lg px-3 text-sm"
            />
            <button
              onClick={applyFilters}
              className="h-10 px-4 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Dealer</th>
              <th className="p-3 text-left">City/State</th>
              <th className="p-3 text-left">Email (editable)</th>
              <th className="p-3 text-center">Invite Status</th>
              <th className="p-3 text-center">Last Invite</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">No dealers found</td></tr>
            )}
            {!loading && rows.map((r) => {
              const id = g(r, "Id");
              const emailVal = emailEdits[id] ?? g(r, "Email") ?? "";
              const status = r.inviteStatus;
              const lastInviteAt = g(r, "LatestInviteCreatedAt")
                ? new Date(g(r, "LatestInviteCreatedAt")).toLocaleString()
                : "—";

              return (
                <tr key={id} className="border-t align-top">
                  <td className="p-3">
                    <div className="font-medium">{g(r, "Name")}</div>
                    <div className="text-xs text-gray-500">ID: {id}</div>
                    {g(r, "VerificationStatus") && (
                      <div className="text-xs text-gray-500">Verification: {g(r, "VerificationStatus")}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <div>{g(r, "City") || "—"}</div>
                    <div className="text-xs text-gray-500">{g(r, "State") || "—"}</div>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <input
                        value={emailVal}
                        onChange={(e) => setEmailEdits((prev) => ({ ...prev, [id]: e.target.value }))}
                        placeholder="dealer contact email"
                        className="h-10 w-64 border border-gray-300 rounded-lg px-3 text-sm"
                      />
                      <button
                        onClick={() => updateEmail(id)}
                        disabled={savingEmailId === id}
                        className="h-10 px-3 rounded-lg bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-50"
                      >
                        {savingEmailId === id ? "Saving…" : "Save"}
                      </button>
                    </div>
                    {g(r, "Email") && (
                      <div className="text-xs text-gray-500 mt-1">Saved: {g(r, "Email")}</div>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    <span
                      className={
                        "inline-block px-2 py-1 rounded-full text-xs " +
                        (status === "Accepted"
                          ? "bg-green-600/10 text-green-800 border border-green-600/30"
                          : status === "Invited"
                          ? "bg-blue-600/10 text-blue-800 border border-blue-600/30"
                          : status === "Expired"
                          ? "bg-yellow-600/10 text-yellow-800 border border-yellow-600/30"
                          : "bg-gray-600/10 text-gray-800 border border-gray-600/30")
                      }
                    >
                      {status}
                    </span>
                  </td>
                  <td className="p-3 text-center">{lastInviteAt}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => sendInvite(id)}
                        disabled={invitingId === id}
                        className="h-10 px-4 rounded-lg bg-blue-700 text-white text-sm hover:bg-blue-800 disabled:opacity-50"
                      >
                        {invitingId === id ? "Sending…" : "Send Invite"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Page {totalPages > 0 ? page : 1} of {Math.max(1, Math.ceil(total / pageSize))} • {total} total
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= Math.max(1, Math.ceil(total / pageSize))}
            className="px-3 py-2 text-sm rounded-lg bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
